import { CommonModule } from '@angular/common';
import { Component, Input, OnInit, QueryList, ViewChildren } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DropdownModule } from 'primeng/dropdown';
import { IFormularioConControles, ControlDTO } from 'lib-base';
import { TablasDinamicasConfig, IColumnDinamico } from '../../../../shared/interfaces/database/tramites_automaticos';
import { ConfiguracionLlavesComponent, LlavesOptions } from '../../../../components/custom-work/other/configuracion-llaves/configuracion-llaves.component';
import { ITipoTramite, ICrudTablaDinamicaConfig, IConfiguracionLLaves } from '../../../../shared/interfaces/database/configuracion';
import { TablasDinamicasConfigService } from '../../../../services/database/tablas-dinamicas-config.service';
import { FormularioService } from '../../../../services/database/formulario.service';
import { firstValueFrom, forkJoin } from 'rxjs';
import { PanelModule } from 'primeng/panel';

@Component({
  selector: 'app-config-insercion-tabla-dinamica',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslateModule, PanelModule,
    DropdownModule, ConfiguracionLlavesComponent],
  template: `
    @for (item of formularioControles; track item.id; let idx = $index) {
      <div class="w-full mb-1">
        <p-panel [header]="item.nombre" 
              [toggleable]="true" [collapsed]="true"
              class="w-full mb-3">
          <div class="grid formgrid p-fluid">
            <div class="field col-12 md:col-12">
              <label for="filtros" class="ml-3">{{'common.tablaDinamica' | translate}}</label>
              <div class="flex gap-1 justify-content-start">
                <p-dropdown [options]="tablasDinamicasConfig" appendTo="body"
                  [ngModel]="getTablaId(item.id)"
                  (ngModelChange)="setTablaId(item.id, $event)"
                  optionLabel="datasetNombre" optionValue="id" [placeholder]="'common.seleccionar'| translate"
                  autoWidth="false" class="flex-grow-1" [filter]="true" [showClear]="true"
                  (onChange)="onTablaParaInsercionChange($event, idx)"
                  (onClear)="onTablaParaInsercionClear(idx)">
                </p-dropdown>                
              </div>
            </div>
            <div class="field col-12 md:col-12">
              <app-configuracion-llaves #TablaParaInsercionIdLlaves 
                [showDelete]="false" [showAdd]="false"
                [headerTitle]="'msg.mapeoDeCamposInsercion' | translate"></app-configuracion-llaves>
            </div>
          </div>
        </p-panel>
      </div>
    }
  `
})
export class ConfigInsercionTablaDinamicaComponent implements OnInit {

  @Input() tipoTramite!: ITipoTramite;

  formularioControles!: IFormularioConControles[];
  tablasDinamicasConfig!: TablasDinamicasConfig[];
  trans: any = {};
  tablaIdMap: Map<number, number> = new Map();

  @ViewChildren('TablaParaInsercionIdLlaves', { read: ConfiguracionLlavesComponent })
  tablaParaInsercionIdLlaves!: QueryList<ConfiguracionLlavesComponent>;
  toLoad: ICrudTablaDinamicaConfig[] | null = null;
  tablasDinamicasConfigReady: boolean = false;
  formularioControlesReady: boolean = false;

  constructor(
    private _tdcs: TablasDinamicasConfigService,
    private _fs: FormularioService,
    private readonly _t: TranslateService
  ) { }

  ngOnInit(): void {
    this.translate();
    this.getTablasDinamicas();
    this.getForm();
  }

  private async translate(): Promise<void> {
    this.trans = await firstValueFrom(forkJoin({
      columnasTablaDinamica: this._t.get('common.columnasTablaDinamica'),
      controlesFormulario: this._t.get('common.controlesFormulario')
    }));
  }

  getTablaId(formId: number): number | null {
    return this.tablaIdMap.get(formId) || null;
  }

  setTablaId(formId: number, tablaId: number | null): void {
    if (tablaId === null) {
      this.tablaIdMap.delete(formId);
    } else {
      this.tablaIdMap.set(formId, tablaId);
    }
  }

  private getTablasDinamicas() {
    this._tdcs.getTablasDinamicas().subscribe({
      next: (data: TablasDinamicasConfig[]) => {
        this.tablasDinamicasConfig = data;
        this.tablasDinamicasConfigReady = true
        this.tryLoadSavedData();
      }
    });
  }

  private getForm() {
    this._fs.getFormularioConControles(this.tipoTramite.formularioId, this.tipoTramite.formularioVersion).subscribe({
      next: (data: IFormularioConControles[]) => {
        this.formularioControles = data;
        this.formularioControlesReady = true;
        this.tryLoadSavedData();
      }
    });
  }

  onTablaParaInsercionChange(event: any, index: number) {
    const value = event.value;
    if (!value) return;

    const component = this.tablaParaInsercionIdLlaves.toArray()[index];
    if (component) {
      this.initializeTablaParaInsercionLlaves(component, value, index, true);
    }
  }

  onTablaParaInsercionClear(index: number) {
    const component = this.tablaParaInsercionIdLlaves.toArray()[index];
    if (component) {
      component.reset();
    }
    const form = this.formularioControles[index];
    this.setTablaId(form.id, null);
  }

  load(data: ICrudTablaDinamicaConfig[]) {
    this.toLoad = data;
    this.tryLoadSavedData();
  }

  private tryLoadSavedData() {
    if (this.toLoad && this.tablasDinamicasConfigReady && this.formularioControlesReady) {
      this.loadSavedData()
    }
  }

  private loadSavedData(): void {
    if (!this.toLoad || !this.formularioControles || !this.tablasDinamicasConfig) return;

    this.toLoad.forEach(config => {
      const formIndex = this.formularioControles.findIndex(
        f => f.id === config.formularioId
      );

      if (formIndex === -1 || !config.tablaDinamicaConfigId) return;

      const form = this.formularioControles[formIndex];
      this.setTablaId(form.id, config.tablaDinamicaConfigId);

      //El componente necesita un pequeño delay para que se renderice correctamente
      setTimeout(() => {
        const component: ConfiguracionLlavesComponent = this.tablaParaInsercionIdLlaves.toArray()[formIndex];
        if (component) {
          this.initializeTablaParaInsercionLlaves(component, config.tablaDinamicaConfigId, formIndex, true);

          let llaves: IConfiguracionLLaves[] = [];
          component.llaves.forEach((llave: IConfiguracionLLaves) => {
            let item = config.llaves.find(savedLlave => savedLlave.name === llave.name);
            if (item) {
              llave.value = item.value;
            }
            llaves.push({ ...llave });

          });
        }
      });
    });
    this.toLoad = null;
  }

  private initializeTablaParaInsercionLlaves(component: ConfiguracionLlavesComponent, tablaId: number, formIndex: number, reset: boolean = true) {
    if (!component) {
      return;
    }

    if (reset) {
      component.reset();
    }

    const selectedTabla = this.tablasDinamicasConfig.find(t => t.id === tablaId);
    if (!selectedTabla) return;

    // Parse the tabla dinamica columns from JSON string
    const columns: IColumnDinamico[] = selectedTabla.datasetColumnas ?? [];

    // Get the specific form controls for this formularioControles item
    const currentForm = this.formularioControles[formIndex];
    if (!currentForm) return;

    let controlesFormulario = currentForm.controls ? [...currentForm.controls] : [];
    if (this.tipoTramite.componentesPersonalizadosParametros) {
      for (const param of this.tipoTramite.componentesPersonalizadosParametros) {
        const control = {
          controlPath: "componente:" + param,
          controlLabel: "componente:" + param
        } as ControlDTO
        controlesFormulario.push(control);
      }
    }

    const keys = {
      data: columns,
      optionLabel: 'displayName',
      optionValue: 'name',
      headerText: this.trans.columnasTablaDinamica
    } as LlavesOptions;

    const values = {
      data: controlesFormulario,
      optionLabel: 'controlLabel',
      optionValue: 'controlPath',
      headerText: this.trans.controlesFormulario
    } as LlavesOptions;

    component.loadOptions(keys, values);

    if (reset) {
      for (const key of columns) {
        const newItem = component.addNew();
        newItem.name = key.name;
      }
    }
  }

  //#region get value
  getComponentValue(): ICrudTablaDinamicaConfig[] | null {
    const result: ICrudTablaDinamicaConfig[] = [];

    this.tablaParaInsercionIdLlaves?.toArray().forEach((component, index) => {
      const form = this.formularioControles[index];
      const llaves = (component.getValues() || []).filter((item: any) => item.value !== '');

      const tablaId = this.getTablaId(form.id);
      if (llaves.length > 0 && tablaId) {
        result.push({
          formularioId: form.id,
          formularioVersion: form.version,
          tablaDinamicaConfigId: tablaId,
          subformulario: form.subformulario,
          tipo: 'insert',
          llaves: llaves
        });
      }
    });

    return result.length > 0 ? result : null;
  }
  //#endregion get value
}
